import type { RealEstateCase } from "../../types";
import {
  MARKET_TIMELINE_EVENT_TYPES,
  type MarketDataOrigin,
  type MarketStatsInput,
  type MarketStatsSnapshot,
  type MarketStatsSource,
  type MarketTimelineEvent,
  type MarketTimelineEventType,
  type MarketTimelineState
} from "./types.ts";

export const emptyMarketTimeline = (): MarketTimelineState => ({
  schemaVersion: "market-timeline-1",
  events: [],
  statsSnapshots: []
});

const safeKey = (value: string) => value.trim().toLocaleLowerCase("sk").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const eventId = (caseId: string, identity: string) => `market:${caseId}:${safeKey(identity)}`;
const snapshotId = (caseId: string, day: number, source: MarketStatsSource) => `market-stats:${caseId}:${day}:${source}`;

export const marketStatsOrigin = (source: MarketStatsSource): MarketDataOrigin => {
  if (source === "nehutelnosti_sk" || source === "bazos_sk" || source === "api") return "PORTAL_DATA";
  return "MANUAL_DATA";
};

export const calendarDayDifference = (from: string, to: string) => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  if (!Number.isFinite(fromDate.getTime()) || !Number.isFinite(toDate.getTime())) return 0;
  const first = Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const second = Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  return Math.max(0, Math.round((second - first) / 86_400_000));
};

export type MarketEventDraft = Omit<MarketTimelineEvent, "id" | "caseId" | "createdAt"> & { identity: string };

export const addMarketEvent = (
  state: MarketTimelineState | undefined,
  caseId: string,
  draft: MarketEventDraft,
  createdAt = draft.occurredAt
): MarketTimelineState => {
  const current = state ?? emptyMarketTimeline();
  const id = eventId(caseId, draft.identity);
  if (current.events.some((event) => event.id === id)) return current;
  const { identity: _identity, ...event } = draft;
  return { ...current, events: [...current.events, { ...event, id, caseId, createdAt }] };
};

export const listedMarketTimeline = (caseItem: RealEstateCase, occurredAt: string) =>
  addMarketEvent(caseItem.marketTimeline, caseItem.id, {
    identity: `listed:${caseItem.listingStartedAt ?? occurredAt}`,
    type: "LISTED",
    occurredAt: caseItem.listingStartedAt ?? occurredAt,
    title: "Zainzerované",
    description: caseItem.askingPrice > 0 ? `Inzertná cena ${caseItem.askingPrice} €` : undefined,
    source: "case_workflow",
    metadata: { askingPrice: caseItem.initialListingPrice ?? (caseItem.askingPrice || undefined) }
  });

export const priceChangedMarketTimeline = (caseItem: RealEstateCase, oldPrice: number, newPrice: number, occurredAt: string) => {
  if (!caseItem.listingStartedAt || oldPrice === newPrice || oldPrice <= 0 || newPrice <= 0) return caseItem.marketTimeline;
  return addMarketEvent(caseItem.marketTimeline, caseItem.id, {
    identity: `price:${occurredAt}:${oldPrice}:${newPrice}`,
    type: "PRICE_CHANGED",
    occurredAt,
    title: "Zmena ceny",
    description: `${oldPrice} € → ${newPrice} €`,
    source: "case_workflow",
    metadata: { oldPrice, newPrice }
  });
};

export const milestoneMarketTimeline = (
  caseItem: RealEstateCase,
  type: "FIRST_CALL" | "FIRST_VIEWING" | "VIEWING" | "BUYER_OFFER" | "RESERVED" | "FINANCING_APPROVED" | "CONTRACT_SIGNED" | "SOLD",
  occurredAt: string,
  metadata: MarketTimelineEvent["metadata"] = {}
) => {
  const titles: Record<typeof type, string> = {
    FIRST_CALL: "Prvý telefonát z trhu",
    FIRST_VIEWING: "Prvá obhliadka",
    VIEWING: "Obhliadka",
    BUYER_OFFER: "Ponuka kupujúceho",
    RESERVED: "Rezervované",
    FINANCING_APPROVED: "Financovanie schválené",
    CONTRACT_SIGNED: "Zmluvy podpísané",
    SOLD: "Predané"
  };
  const singular = type === "FIRST_CALL" || type === "FIRST_VIEWING" || type === "RESERVED" || type === "SOLD";
  return addMarketEvent(caseItem.marketTimeline, caseItem.id, {
    identity: singular ? type : `${type}:${occurredAt}`,
    type,
    occurredAt,
    title: titles[type],
    source: type === "FIRST_CALL" || type === "FIRST_VIEWING" || type === "VIEWING" ? "crm" : "case_workflow",
    metadata
  });
};

export const safelyDerivedMarketTimeline = (caseItem: RealEstateCase): MarketTimelineState => {
  let state = caseItem.marketTimeline ?? emptyMarketTimeline();
  if (caseItem.listingStartedAt) {
    state = listedMarketTimeline({ ...caseItem, marketTimeline: state }, caseItem.listingStartedAt);
    const listingTime = Date.parse(caseItem.listingStartedAt);
    const historicalActivities = [...caseItem.timeline]
      .filter((activity) => Date.parse(activity.createdAt) >= listingTime)
      .sort((first, second) => Date.parse(first.createdAt) - Date.parse(second.createdAt));
    const firstCall = historicalActivities.find((activity) => activity.type === "call" || activity.activityKind === "call" || activity.outcome);
    if (firstCall) state = milestoneMarketTimeline({ ...caseItem, marketTimeline: state }, "FIRST_CALL", firstCall.createdAt);
    const viewings = historicalActivities.filter((activity) => activity.activityKind === "viewing");
    viewings.forEach((activity, index) => {
      state = milestoneMarketTimeline({ ...caseItem, marketTimeline: state }, index === 0 ? "FIRST_VIEWING" : "VIEWING", activity.createdAt);
    });
  }
  if (caseItem.soldAt && caseItem.realizedPrice) {
    state = milestoneMarketTimeline({ ...caseItem, marketTimeline: state }, "SOLD", caseItem.soldAt, { realizedPrice: caseItem.realizedPrice });
  }
  return state;
};

export const addMarketStatsSnapshot = (caseItem: RealEstateCase, input: MarketStatsInput) => {
  const current = caseItem.marketTimeline ?? emptyMarketTimeline();
  const dayFromListing = caseItem.listingStartedAt ? calendarDayDifference(caseItem.listingStartedAt, input.capturedAt) : 0;
  const id = snapshotId(caseItem.id, dayFromListing, input.source);
  const existing = current.statsSnapshots.find((snapshot) => snapshot.id === id);
  if (existing) return { state: current, snapshot: existing, created: false };

  const fields = ["views", "detailViews", "calls", "emails", "saved", "viewings"] as const;
  const fieldSources = Object.fromEntries(fields.filter((field) => input[field] !== undefined).map((field) => [field, input.source]));
  const snapshot: MarketStatsSnapshot = {
    id,
    caseId: caseItem.id,
    capturedAt: input.capturedAt,
    dayFromListing,
    source: input.source,
    origin: marketStatsOrigin(input.source),
    views: input.views,
    detailViews: input.detailViews,
    calls: input.calls,
    emails: input.emails,
    saved: input.saved,
    viewings: input.viewings,
    fieldSources,
    askingPriceAtCapture: caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined,
    notes: input.notes?.trim() || undefined,
    screenshot: input.screenshot,
    createdAt: input.capturedAt
  };
  const withSnapshot = { ...current, statsSnapshots: [...current.statsSnapshots, snapshot] };
  let state = addMarketEvent(withSnapshot, caseItem.id, {
    identity: `stats:${id}`,
    type: "MARKET_STATS_CAPTURED",
    occurredAt: input.capturedAt,
    title: dayFromListing === 3 ? "Prvé 3 dni" : dayFromListing === 7 ? "7-dňové vyhodnotenie" : `Štatistiky – deň ${dayFromListing}`,
    description: [input.views !== undefined ? `${input.views} zobrazení` : "", input.calls !== undefined ? `${input.calls} telefonátov` : "", input.viewings !== undefined ? `${input.viewings} obhliadok` : ""].filter(Boolean).join(" · "),
    source: input.source === "manual" || input.source === "screenshot" ? "manual" : "portal",
    metadata: { statsSnapshotId: id, askingPrice: snapshot.askingPriceAtCapture, sourceLabel: input.source }
  });
  const previousCalls = Math.max(0, ...current.statsSnapshots.map((item) => item.calls ?? 0));
  if ((input.calls ?? 0) > 0 && previousCalls === 0) {
    state = addMarketEvent(state, caseItem.id, {
      identity: "FIRST_CALL",
      type: "FIRST_CALL",
      occurredAt: input.capturedAt,
      title: "Prvý telefonát z trhu",
      description: `Celkom ${input.calls} telefonátov podľa snapshotu`,
      source: input.source === "manual" || input.source === "screenshot" ? "manual" : "portal",
      metadata: { count: input.calls, statsSnapshotId: id, sourceLabel: input.source }
    });
  }
  const previousViewings = Math.max(0, ...current.statsSnapshots.map((item) => item.viewings ?? 0));
  if ((input.viewings ?? 0) > previousViewings) {
    state = addMarketEvent(state, caseItem.id, {
      identity: previousViewings === 0 ? "FIRST_VIEWING" : `VIEWING:${id}`,
      type: previousViewings === 0 ? "FIRST_VIEWING" : "VIEWING",
      occurredAt: input.capturedAt,
      title: input.viewings === 1 ? "Prvá obhliadka" : `${input.viewings} obhliadky`,
      description: "Celkový počet podľa štatistického snapshotu",
      source: input.source === "manual" || input.source === "screenshot" ? "manual" : "portal",
      metadata: { count: input.viewings, statsSnapshotId: id, sourceLabel: input.source }
    });
  }
  return { state, snapshot, created: true };
};

const finiteOptional = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
const text = (value: unknown) => typeof value === "string" && value.trim() ? value : undefined;

export const normalizeMarketTimeline = (value: unknown): MarketTimelineState | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const source = value as Record<string, unknown>;
  const events = Array.isArray(source.events) ? source.events.flatMap((item): MarketTimelineEvent[] => {
    if (!item || typeof item !== "object") return [];
    const event = item as Record<string, unknown>;
    if (typeof event.id !== "string" || typeof event.caseId !== "string" || !MARKET_TIMELINE_EVENT_TYPES.includes(event.type as MarketTimelineEventType) || typeof event.occurredAt !== "string" || typeof event.title !== "string") return [];
    return [{ id: event.id, caseId: event.caseId, type: event.type as MarketTimelineEventType, occurredAt: event.occurredAt, title: event.title, description: text(event.description), source: ["case_workflow", "crm", "manual", "portal", "market"].includes(String(event.source)) ? event.source as MarketTimelineEvent["source"] : "manual", metadata: event.metadata && typeof event.metadata === "object" ? event.metadata as MarketTimelineEvent["metadata"] : undefined, createdAt: typeof event.createdAt === "string" ? event.createdAt : event.occurredAt }];
  }) : [];
  const statsSnapshots = Array.isArray(source.statsSnapshots) ? source.statsSnapshots.flatMap((item): MarketStatsSnapshot[] => {
    if (!item || typeof item !== "object") return [];
    const snapshot = item as Record<string, unknown>;
    if (typeof snapshot.id !== "string" || typeof snapshot.caseId !== "string" || typeof snapshot.capturedAt !== "string") return [];
    const statsSource = ["nehutelnosti_sk", "bazos_sk", "manual", "api", "screenshot", "other"].includes(String(snapshot.source)) ? snapshot.source as MarketStatsSource : "manual";
    return [{ id: snapshot.id, caseId: snapshot.caseId, capturedAt: snapshot.capturedAt, dayFromListing: finiteOptional(snapshot.dayFromListing) ?? 0, source: statsSource, origin: ["PORTAL_DATA", "CRM_DATA", "MARKET_DATA", "MANUAL_DATA"].includes(String(snapshot.origin)) ? snapshot.origin as MarketDataOrigin : marketStatsOrigin(statsSource), views: finiteOptional(snapshot.views), detailViews: finiteOptional(snapshot.detailViews), calls: finiteOptional(snapshot.calls), emails: finiteOptional(snapshot.emails), saved: finiteOptional(snapshot.saved), viewings: finiteOptional(snapshot.viewings), fieldSources: snapshot.fieldSources && typeof snapshot.fieldSources === "object" ? snapshot.fieldSources as MarketStatsSnapshot["fieldSources"] : {}, askingPriceAtCapture: finiteOptional(snapshot.askingPriceAtCapture), notes: text(snapshot.notes), screenshot: snapshot.screenshot && typeof snapshot.screenshot === "object" ? snapshot.screenshot as MarketStatsSnapshot["screenshot"] : undefined, createdAt: typeof snapshot.createdAt === "string" ? snapshot.createdAt : snapshot.capturedAt }];
  }) : [];
  return { schemaVersion: "market-timeline-1", events, statsSnapshots };
};
